import React from 'react';
import OfferCard from '../card/OfferCard';
import { formatPrice } from '../utils';
import { summarizeCompetingGroup, isLiveOffer } from './competingOffers';
import CompetingOffersTable from './CompetingOffersTable';
import './CompetingOffersGroup.css';

/**
 * CompetingOffersGroup — the offers bidding against each other on one of the
 * seller's listings, shown together instead of scattered through a flat list.
 * A header names the property and ranks the field (highest live bid + the spread
 * to the lowest), the strongest offer gets a "Highest offer" flag, and a cue
 * spells out that accepting one offer ends the rest (the backend auto-declines
 * the competing offers on accept).
 *
 * Only rendered for sellers, and only for a property with 2+ offers — a lone
 * offer has nothing to compare against and stays an ordinary card.
 */
export default function CompetingOffersGroup({
  group,
  currentRole = 'seller',
  onAction,
  onViewHouse,
  onViewHistory,
}) {
  const offers = group?.offers || [];
  // Count and compare against the full set for this property (summaryOffers)
  // when it's given, so a status filter that hides a rival doesn't understate
  // the field; fall back to the displayed offers when it isn't.
  const summaryOffers = group?.summaryOffers || offers;
  const summary = summarizeCompetingGroup(summaryOffers);
  const hiddenByFilter = Math.max(0, summary.total - offers.length);
  // The still-in-play offers, lined up for a terms comparison. Taken from the
  // full set so the table stays complete even if a filter hides a card below.
  const liveContenders = summaryOffers.filter(isLiveOffer);
  const hasAccepted = summary.acceptedId != null;
  // Only flag a leader while there's a live contest to win; once one is accepted
  // the card's own "accepted" status already says who won.
  const showLeader = !hasAccepted && summary.liveCount >= 2 && summary.leaderId != null;

  const cue = hasAccepted
    ? 'Under contract — the other offers on this listing were automatically declined.'
    : summary.liveCount >= 2
      ? 'Accepting one offer automatically declines the others on this listing.'
      : null;

  const groupKey = group?.propertyId ?? group?.key ?? 'group';

  return (
    <section className="competing-group" data-testid={`competing-group-${groupKey}`} aria-label={`Competing offers on ${group?.propertyAddress || 'this listing'}`}>
      <header className="competing-group__head">
        <div className="competing-group__title">
          <h3 className="competing-group__address">
            {group?.propertyAddress || 'This listing'}
          </h3>
          <span className="competing-group__count" data-testid="competing-count">
            {summary.total} offers
          </span>
          {hiddenByFilter > 0 ? (
            <span className="competing-group__filtered" data-testid="competing-filtered">
              {hiddenByFilter} hidden by your filter
            </span>
          ) : null}
        </div>
        {summary.highest != null ? (
          <div className="competing-group__compare" data-testid="competing-compare">
            <span className="competing-group__stat">
              Highest live
              <strong data-testid="competing-highest">{formatPrice(summary.highest)}</strong>
            </span>
            {summary.spread > 0 ? (
              <span className="competing-group__stat">
                Spread
                <strong data-testid="competing-spread">{formatPrice(summary.spread)}</strong>
              </span>
            ) : null}
            <span className="competing-group__stat">
              In play
              <strong>{summary.liveCount}</strong>
            </span>
          </div>
        ) : null}
      </header>

      {cue ? (
        <p
          className={`competing-group__cue${hasAccepted ? ' competing-group__cue--contracted' : ''}`}
          role="note"
          data-testid="competing-cue"
        >
          {cue}
        </p>
      ) : null}

      <CompetingOffersTable offers={liveContenders} />

      <ul className="offers-list competing-group__cards">
        {offers.map((offer, i) => {
          const isLeader = showLeader && offer.id === summary.leaderId;
          return (
            <li key={offer.id} className="offers-list__item competing-group__card">
              {isLeader ? (
                <span className="competing-group__leader-flag" data-testid="competing-leader-flag">
                  ★ Highest offer
                </span>
              ) : null}
              <OfferCard
                offer={offer}
                currentRole={currentRole}
                onAction={onAction}
                onViewHouse={onViewHouse}
                onViewHistory={onViewHistory}
                index={i}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
